import {
  isPosicaoZeroed,
  resolveAtivoPl,
  resolveGrupoPl,
} from '@modules/consolidator/utils/posicao-pl-fallback/posicao-pl-fallback'

export const buildAssetGroups = (walletAnalysis, getColor, options) => {
  const expanded = !!(options && options.expanded)
  const idCliente = (options && options.idCliente) || null
  const sourceKey = expanded ? 'posicao_expandida' : 'posicao_completa'
  const posicao = walletAnalysis && walletAnalysis[sourceKey]
  if (!posicao || typeof posicao !== 'object') return {}

  const grupos = Array.isArray(posicao.grupos) ? posicao.grupos : []
  if (!grupos.length) return {}

  const zeroed = isPosicaoZeroed(walletAnalysis)
  const result = {}
  grupos.forEach((group, index) => {
    if (!group) return
    const groupName = group.grupo || ''
    if (!groupName) return
    const ativos = Array.isArray(group.ativos) ? group.ativos : []
    result[groupName] = {
      nome: groupName,
      color: getColor ? getColor(groupName, index) : '',
      Aplicação: resolveGrupoPl(group, zeroed),
      peso: group.peso || 0,
      ativos: ativos.map((ativo) =>
        buildLegacyAssetRow(ativo, groupName, idCliente, zeroed)
      ),
    }
  })
  return result
}

const buildLegacyAssetRow = (ativo, groupName, idCliente, zeroed) => {
  if (!ativo) return {}
  return {
    ...ativo,
    ...buildAssetIdentity(ativo, idCliente),
    ...buildAssetLabels(ativo),
    ...buildAssetAmounts(ativo, zeroed),
    ...buildAssetClassification(ativo, groupName),
    ...buildAssetContext(ativo),
    ...buildAssetQuantitative(ativo),
    ...buildAssetDates(ativo),
    ...buildAssetMetadata(ativo),
    ...buildAssetFlags(ativo),
  }
}

const BACKEND_INTERNAL_COMMENT_FLAGS = new Set([
  'TOTAL_WITHDRAWAL',
  'PARTIAL_WITHDRAWAL',
])

const resolveAssetIdentity = (ativo) => {
  const id = ativo.id == null ? ativo.id_ativo : ativo.id
  const idAtivo = ativo.id_ativo == null ? null : ativo.id_ativo
  return { id, idAtivo }
}

const sanitizeComentario = (raw) => {
  if (!raw) return null
  if (BACKEND_INTERNAL_COMMENT_FLAGS.has(raw)) return null
  return raw
}

const buildAssetIdentity = (ativo, idCliente) => {
  const { id, idAtivo } = resolveAssetIdentity(ativo)
  return {
    id,
    id_ativo: idAtivo,
    id_ativo_cliente: null,
    // ★ Sem `idCliente` na opcao, o dono e o do PROPRIO ativo. A tela do cliente
    // sempre passa a opcao (e la o dono coincide), entao nada muda pra ela; a
    // carteira do GRUPO nao tem um dono unico, e zerar aqui fazia toda acao de
    // linha (editar, detalhes) gravar sob o cliente selecionado no shell, nao
    // sob o titular do ativo.
    id_cliente: idCliente || ativo.id_cliente || null,
    nome_cliente: ativo.nome_cliente || ativo.membro || '',
    id_conta: ativo.id_conta || null,
    uuid_ativo: ativo.uuid_ativo || null,
    external_id: ativo.external_id || null,
    isin: ativo.isin || null,
  }
}

const buildAssetLabels = (ativo) => ({
  nome_ativo: ativo.nome_ativo || '',
  referencia: ativo.referencia || ativo.nome_ativo || '',
  'Ativo + Taxa': ativo.nome_ativo || '',
  taxa: ativo.taxa || '',
})

const buildAssetAmounts = (ativo, zeroed) => ({
  Aplicação: resolveAtivoPl(ativo, zeroed),
  ativos_atuais: resolveAtivoPl(ativo, zeroed),
  'Saldo Bruto': ativo.saldo_bruto || ativo.pl || 0,
  'Saldo Líquido': ativo.saldo_liquido || ativo.pl || 0,
  pl_atual_offshore: ativo.pl_atual_offshore || null,
  peso_no_portfolio: ativo.peso_no_portfolio || 0,
  peso_no_grupo: ativo.peso_no_grupo || 0,
})

const buildAssetClassification = (ativo, groupName) => ({
  Tipo: ativo.tipo || null,
  subtipo: ativo.subtipo || null,
  classe: ativo.classe || null,
  subclasse: ativo.subclasse || null,
  CI: ativo.classe || null,
  _office_class: groupName,
})

const buildAssetContext = (ativo) => ({
  corretora: ativo.corretora || null,
  nome_portfolio: ativo.nome_portfolio || null,
  emissor: ativo.emissor || null,
  setor: ativo.setor || null,
  tags: ativo.tags || null,
  tipo_investimento: ativo.tipo_investimento || null,
  moeda: ativo.moeda || null,
  exchange: ativo.exchange || null,
  origem: ativo.origem || null,
})

const buildAssetQuantitative = (ativo) => ({
  quantidade: ativo.quantidade == null ? null : ativo.quantidade,
  pu: ativo.pu == null ? null : ativo.pu,
  preco_medio: ativo.preco_medio == null ? null : ativo.preco_medio,
  aplicacao_inicial:
    ativo.aplicacao_inicial == null ? null : ativo.aplicacao_inicial,
  lucro_prejuizo: ativo.lucro_prejuizo == null ? null : ativo.lucro_prejuizo,
  rendimento: ativo.rendimento == null ? null : ativo.rendimento,
  liquidez: ativo.liquidez == null ? null : ativo.liquidez,
})

const buildAssetDates = (ativo) => ({
  data_aplicacao_inicial: ativo.data_aplicacao_inicial || null,
  data_vencimento: ativo.data_vencimento || null,
  data_cotizacao_inicial: ativo.data_cotizacao_inicial || null,
  data_referencia_pl: ativo.data_referencia_pl || null,
  data_referencia_cotizacao: ativo.data_referencia_cotizacao || null,
  _d_referencia_pl: ativo._d_referencia_pl || ativo.data_referencia_pl || null,
  _d_referencia_cotizacao:
    ativo._d_referencia_cotizacao || ativo.data_referencia_cotizacao || null,
})

const buildAssetMetadata = (ativo) => ({
  rating: ativo.rating || null,
  comentario: sanitizeComentario(ativo.comentario),
  recomendacao: ativo.recomendacao || null,
})

const buildAssetFlags = (ativo) => ({
  posicao_comprada: ativo.posicao_comprada !== false,
  adicionado: ativo.adicionado || false,
  editado: ativo.editado || false,
  atual: ativo.atual !== false,
  isento: ativo.isento || null,
  aviso_resgate_total: ativo.aviso_resgate_total || false,
  campos_faltantes: ativo.campos_faltantes || null,
  marcacao_na_curva: !!ativo.marcacao_na_curva,
})
